<script lang="ts">
  import type { PageData } from './$types'
  import {
    Button,
    Carousel,
    Divider,
    Facility,
    Icon,
    Markdown,
    Skeleton,
    StructuredData,
    TierBadge,
  } from '@bruce/components'
  import { utils } from '$lib/utils'
  import { STUDIO_STRINGS } from './strings'
  import RatingDistribution from '$lib/components/RatingDistribution.svelte'
  import Reviews from '$lib/components/Reviews.svelte'
  import Map from '$lib/components/Map.svelte'
  import StudioCard from '$lib/components/StudioCard.svelte'
  import { PUBLIC_ENVIRONMENT } from '$env/static/public'

  export let data: PageData

  $: ({
    lang,
    studio,
    imageUrl,
    classes,
    reviews,
    openingHours,
    newOpeningHours,
    exploreMoreStudios,
    facilities,
  } = data)

  let image: HTMLImageElement | undefined = undefined
  let imgLoading = true

  $: if (image?.complete) {
    imgLoading = false
  }

  $: localeRoute = utils.getRoute(lang)
  let expandDescription = false

  function toggleExpandDescription() {
    expandDescription = !expandDescription
  }
</script>

{#if studio.rating}
  <StructuredData
    schema={{
      '@type': 'AggregateRating',
      itemReviewed: {
        '@type': 'LocalBusiness',
        image: `${imageUrl}/fl_progressive/${studio.photo}`,
        name: studio.name,
        address: {
          '@type': 'PostalAddress',
          streetAddress: studio.studio_addresses?.[0]?.address.address || '',
          addressLocality: studio.city.name,
          addressRegion: studio.vicinity || '',
          addressCountry: studio.city.country?.code || '',
        },
      },
      ratingValue: studio.rating.average / 100,
      ratingCount: studio.rating.total,
    }}
  />
{/if}
<div class="container">
  {#if studio.photo}
    <section class="photos">
      {#if studio.studio_photos}
        <Carousel cycle>
          <img
            src={`${imageUrl}/fl_progressive/${studio.photo}`}
            alt={studio.name}
          />
          {#if studio.studio_photos}
            {#each studio.studio_photos as photo (photo.id)}
              <img
                src={`${imageUrl}/fl_progressive/${photo.photo.file_id}`}
                alt={studio.name}
              />
            {/each}
          {/if}
        </Carousel>
      {:else}
        <div class="single-image-container">
          <img
            src={`${imageUrl}/fl_progressive/${studio.photo}`}
            alt={studio.name}
            bind:this={image}
            on:load={() => (imgLoading = false)}
            loading="lazy"
            class:imgLoading
          />
          <Skeleton visible={imgLoading} />
        </div>
      {/if}
    </section>
  {/if}
  <h1>{studio.name}</h1>
  <div class="meta">
    <TierBadge tier={studio.tier.name} />
    {#if studio.rating}
      <div class="rating">
        <Icon icon="ph-star-fill" />
        <span>{Math.round(studio.rating.average / 10) / 10}</span>
      </div>
      <Divider orientation="vertical" />
    {/if}
    <div class="vicinity">
      {studio.vicinity}
    </div>
    <Divider orientation="vertical" />
    <div class="categories">
      {studio.categories.map((category) => category.title).join(', ')}
    </div>
  </div>
  {#if studio.description}
    <section class="description">
      <div class:expandDescription>
        <Markdown source={studio.description} />
      </div>
      <Button
        type="button"
        variant="text"
        rounded
        on:click={toggleExpandDescription}
      >
        {expandDescription
          ? STUDIO_STRINGS[lang].SHOW_LESS
          : STUDIO_STRINGS[lang].READ_MORE}
      </Button>
    </section>
    <Divider />
  {/if}
  {#if studio.studio_facilities.length > 0}
    <section>
      <h3>{STUDIO_STRINGS[lang].FACILITIES}</h3>
      <div class="facilities">
        {#each facilities as facility (facility.id)}
          <Facility facility={facility.facility} />
        {/each}
      </div>
    </section>
    <Divider />
  {/if}
  {#if newOpeningHours.length > 0 && PUBLIC_ENVIRONMENT !== 'production'}
    <section>
      <h3>{STUDIO_STRINGS[lang].OPENING_HOURS}</h3>
      <ul class="opening-hours">
        {#each newOpeningHours as opening, index (opening.id)}
          {@const startTime = new Date(opening.start).toLocaleTimeString(lang, {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: studio.time_zone,
          })}
          {@const endTime = new Date(opening.end).toLocaleTimeString(lang, {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: studio.time_zone,
          })}
          <li class="opening-hour">
            <div>
              {index === 0 &&
              utils.toTitleCase(
                new Date(opening.start).toLocaleDateString(lang, {
                  weekday: 'long',
                  timeZone: studio.time_zone,
                }),
              ) ===
                utils.toTitleCase(
                  new Date().toLocaleDateString(lang, {
                    weekday: 'long',
                    timeZone: studio.time_zone,
                  }),
                )
                ? STUDIO_STRINGS[lang].TODAY
                : utils.toTitleCase(
                    new Date(opening.start).toLocaleDateString(lang, {
                      weekday: 'long',
                      timeZone: studio.time_zone,
                    }),
                  )}
            </div>
            <div>
              <div>
                {startTime === endTime ? '00:00' : startTime} -
                {endTime === startTime ? '24:00' : endTime}
              </div>
            </div>
          </li>
        {/each}
      </ul>
    </section>
    <Divider />
  {/if}
  {#if studio.has_openings && (PUBLIC_ENVIRONMENT === 'production' || newOpeningHours.length === 0)}
    <section>
      <h3>{STUDIO_STRINGS[lang].OPENING_HOURS}</h3>
      <ul class="opening-hours">
        {#each openingHours as opening, index (opening.id)}
          <li class="opening-hour">
            <div>
              {index === 0 &&
              utils.toTitleCase(
                new Date(opening.start_time).toLocaleDateString(lang, {
                  weekday: 'long',
                  timeZone: studio.time_zone,
                }),
              ) ===
                utils.toTitleCase(
                  new Date().toLocaleDateString(lang, {
                    weekday: 'long',
                    timeZone: studio.time_zone,
                  }),
                )
                ? STUDIO_STRINGS[lang].TODAY
                : utils.toTitleCase(
                    new Date(opening.start_time).toLocaleDateString(lang, {
                      weekday: 'long',
                      timeZone: studio.time_zone,
                    }),
                  )}
            </div>
            <div>
              <div>
                {new Date(opening.start_time).toLocaleTimeString(lang, {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: studio.time_zone,
                })} -
                {new Date(opening.end_time).toLocaleTimeString(lang, {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: studio.time_zone,
                })}
              </div>
            </div>
          </li>
        {/each}
      </ul>
    </section>
    <Divider />
  {/if}
  {#if studio.has_classes}
    <section>
      <h3>{STUDIO_STRINGS[lang].CLASSES}</h3>
      {#if classes.length > 0}
        <h4>{STUDIO_STRINGS[lang].TODAY}</h4>
        <ul class="classes">
          {#each classes as classItem (classItem.id)}
            <li class="class">
              <div class="time">
                <span>
                  {new Date(classItem.start_time).toLocaleTimeString(lang, {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: studio.time_zone,
                  })}
                </span>
                <span>
                  {`${
                    (Date.parse(classItem.end_time) -
                      Date.parse(classItem.start_time)) /
                    (1000 * 60)
                  } min`}
                </span>
              </div>
              <div class="title-and-instructor">
                <span class="title">
                  {classItem.title}
                </span>
                {#if classItem.instructor}
                  <span>
                    {classItem.instructor}
                  </span>
                {/if}
              </div>
            </li>
          {/each}
        </ul>
      {:else}
        <p>{STUDIO_STRINGS[lang].NO_CLASSES}</p>
      {/if}
      <Button
        href={localeRoute('/app')}
        rounded
        variant="outlined"
        prependIcon="ph-device-mobile"
      >
        {STUDIO_STRINGS[lang].SEE_IN_APP}
      </Button>
    </section>
    <Divider />
  {/if}
  {#if studio.rating}
    <section>
      <h3>{STUDIO_STRINGS[lang].REVIEWS}</h3>
      <RatingDistribution rating={studio.rating} />
      {#if reviews}
        <Divider />
        <Reviews {reviews} {imageUrl} />
      {/if}
    </section>
    <Divider />
  {/if}
  {#if studio.studio_addresses?.[0]}
    <section class="location">
      <h3>{STUDIO_STRINGS[lang].LOCATION}</h3>
      <div class="map">
        <Map
          center={{
            lat: studio.studio_addresses[0].address.latitude,
            lng: studio.studio_addresses[0].address.longitude,
          }}
          streetViewControl={false}
          mapTypeControl={false}
          zoomControl={false}
          clickableIcons={false}
          zoom={14}
          {imageUrl}
          markers={[
            {
              lat: studio.studio_addresses[0].address.latitude,
              lng: studio.studio_addresses[0].address.longitude,
            },
          ]}
        />
      </div>
      <div>{studio.studio_addresses[0].address.address}</div>
      <Button
        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(studio.studio_addresses[0].address.address)}&travelmode=transit`}
        target="_blank"
        rounded
        variant="outlined"
        rel="noreferrer"
        prependIcon="ph-arrow-elbow-up-right"
      >
        {STUDIO_STRINGS[lang].DIRECTIONS}
      </Button>
    </section>
    <Divider />
  {/if}
  <section>
    <h3>{STUDIO_STRINGS[lang].EXPLORE} {studio.city.name}</h3>
    <ul class="explore-studios">
      {#each exploreMoreStudios as studio (studio.id)}
        <li>
          <StudioCard {imageUrl} {studio} vicinity categories rating />
        </li>
      {/each}
    </ul>
  </section>
</div>

<style lang="scss">
  @use '@bruce/assets/css/breakpoints';

  .single-image-container {
    position: relative;
    border-radius: 0.5em;

    img {
      width: 100%;
      height: 100%;
      &.imgLoading {
        opacity: 0;
      }
    }
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: 1em;
    max-width: 800px;

    @media screen and (max-width: breakpoints.$breakpoint-sm) {
      width: calc(100% - 32px);
    }

    .photos {
      width: 100%;
      height: max-content;

      img {
        aspect-ratio: 16 / 10;
        object-fit: cover;
        border-radius: 0.5em;
      }
    }

    @media screen and (max-width: breakpoints.$breakpoint-sm) {
      h1 {
        margin: 0.5em 0;
      }
    }

    .meta {
      display: flex;
      align-items: center;
      gap: 1em;

      @media screen and (max-width: breakpoints.$breakpoint-sm) {
        font-size: 14px;
      }

      .rating,
      .vicinity {
        display: flex;
        align-items: center;
        gap: 0.25em;
      }

      .rating,
      .vicinity,
      .categories {
        font-size: 20px;

        @media screen and (max-width: breakpoints.$breakpoint-sm) {
          font-size: 14px;
        }
      }

      .categories {
        overflow: hidden;
        text-wrap: nowrap;
        text-overflow: ellipsis;
        min-width: 0;
      }
    }

    .description {
      display: flex;
      flex-direction: column;
      gap: 0.5em;

      div {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        text-align: left;
        margin: 0;

        &.expandDescription {
          line-clamp: unset;
          -webkit-line-clamp: unset;
        }
      }
    }

    h3 {
      margin-top: 0;
    }

    .facilities {
      display: flex;

      @media screen and (max-width: breakpoints.$breakpoint-sm) {
        flex-direction: column;
      }
    }

    .opening-hours,
    .classes {
      display: flex;
      flex-direction: column;
      list-style: none;
      padding: 0;
    }

    .opening-hours {
      gap: 0.5em;
      max-width: 300px;

      .opening-hour {
        display: flex;
        justify-content: space-between;
        gap: 2em;
      }
    }

    .classes {
      gap: 2em;
      margin-bottom: 2em;

      .class {
        display: flex;
        gap: 2em;
        font-size: 20px;

        .time,
        .title-and-instructor {
          display: flex;
          flex-direction: column;
          gap: 0.25em;
        }

        .time {
          width: 100px;
        }

        .title-and-instructor {
          .title {
            font-weight: bold;
          }
        }
      }
    }

    .location {
      display: flex;
      flex-direction: column;
      gap: 1em;

      h3 {
        margin: 0;
      }

      .map {
        height: 480px;
      }
    }

    .explore-studios {
      display: flex;
      gap: 1em;
      list-style-type: none;
      margin: 0;
      padding: 0;
      width: 100%;

      @media screen and (max-width: breakpoints.$breakpoint-sm) {
        flex-direction: column;
      }

      li {
        flex: 1;
      }
    }
  }
</style>
